import { stringify } from 'qs'

const isPresident = ref(false)
const presidentEntities = ref([])
const loading = ref(false)

const fetchPresidentEntities = async () => {
  if (loading.value) return

  const { userData, barData, firmData, userQuality } = useAuthData()
  const quality = userQuality.value || 'individuel'

  const qualitiesMap = {
    individuel: { endpoint: 'users-data', documentId: userData.value?.documentId },
    collectif: { endpoint: 'bars', documentId: barData.value?.documentId },
    firme: { endpoint: 'firms', documentId: firmData.value?.documentId }
  }

  const endpoint = qualitiesMap[quality]?.endpoint
  const activeDocumentId = qualitiesMap[quality]?.documentId

  if (!activeDocumentId) return

  loading.value = true
  try {
    const config = useRuntimeConfig()
    const authStore = useAuthStore()

    const populate = {
      user_commission_roles: {
        filters: {
          $or: [{ quality: { $eq: 'president' } }, { role: { $containsi: 'responsable_w' } }],
          isEnded: { $eq: false },
          deleted: { $ne: true }
        },
        populate: {
          commission: { fields: ['documentId', 'title'] },
          committee: { fields: ['documentId', 'title'] }
        }
      }
    }

    // Mandates and directory_address only for individual users
    if (quality === 'individuel') {
      populate.directory_address = {
        populate: { country: { fields: ['documentId', 'name'], populate: { flag: true } } }
      }
      populate.mandates = {
        filters: {
          category: { $in: ['president_de_comite_national', 'representant_national'] },
          archived: { $eq: false },
          deleted: { $ne: true },
          ended_mandate: { $eq: false }
        },
        populate: {
          'national-committees': { fields: ['documentId', 'title'] }
        }
      }
    }

    const query = stringify({ populate }, { encodeValuesOnly: true })

    const response = await $fetch(`${config.public.strapiApi}/${endpoint}/${activeDocumentId}?${query}`, {
      headers: {
        Authorization: `Bearer ${authStore.token}`
      }
    })

    const data = response.data
    const entities = []
    const seen = new Set()

    // Commissions & committees from user_commission_roles
    // Sort so president-quality roles come first
    const sortedRoles = [...(data?.user_commission_roles || [])].sort((a, b) => {
      if (a.quality === 'president' && b.quality !== 'president') return -1
      if (a.quality !== 'president' && b.quality === 'president') return 1
      return 0
    })

    for (const role of sortedRoles) {
      const roleType = role.quality === 'president' ? 'president' : 'responsable'
      for (const type of ['commission', 'committee']) {
        const entity = role[type]
        if (entity?.documentId && !seen.has(`${type}-${entity.documentId}`)) {
          seen.add(`${type}-${entity.documentId}`)
          entities.push({
            documentId: entity.documentId,
            title: entity.title,
            type,
            roleType
          })
        }
      }
    }

    // National committees and countries from mandates (individual users only)
    if (quality === 'individuel') {
      const representativeCountry = data?.directory_address?.country || null

      for (const mandate of data?.mandates || []) {
        if (mandate.category === 'president_de_comite_national') {
          const ncs = mandate['national-committees'] || []
          for (const nc of ncs) {
            if (nc?.documentId && !seen.has(`national_committee-${nc.documentId}`)) {
              seen.add(`national_committee-${nc.documentId}`)
              entities.push({
                documentId: nc.documentId,
                title: nc.title,
                type: 'national_committee',
                roleType: 'president'
              })
            }
          }
        } else if (mandate.category === 'representant_national' && representativeCountry?.documentId) {
          const key = `country-${representativeCountry.documentId}`
          if (!seen.has(key)) {
            seen.add(key)
            entities.push({
              documentId: representativeCountry.documentId,
              title: representativeCountry.name,
              thumbnail: representativeCountry.flag || null,
              type: 'country',
              roleType: 'representative'
            })
          }
        }
      }
    }

    presidentEntities.value = entities
    isPresident.value = entities.length > 0
  } catch (error) {
    isPresident.value = false
    presidentEntities.value = []
  } finally {
    loading.value = false
  }
}

function isPresidentOf(entity, type) {
  if (!entity?.documentId || !type) return false
  return presidentEntities.value.some((e) => e.documentId === entity.documentId && e.type === type)
}

function getPresidentEntity(entity) {
  if (!entity?.documentId) return null
  return presidentEntities.value.find((e) => e.documentId === entity.documentId) || null
}

export const useIsPresident = () => {
  return {
    isPresident,
    presidentEntities,
    loading,
    fetchPresidentEntities,
    isPresidentOf,
    getPresidentEntity
  }
}
